import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, FieldArray } from 'redux-form';
import SelectField from '../../fields/SelectField/SelectField';
import TextField from '../../fields/TextField/TextField';
import ToggleField from '../../fields/ToggleField/ToggleField';
import NumberField from '../../fields/NumberField/NumberField';
import AddOnField from '../../fields/AddOnField/AddOnField';
import TextAreaField from 'shared-components/reduxForms/fields/TextAreaField/TextAreaField';
import { CREATE_ITEM_FORM } from '../../constants/formNames';
import AddFees from '../../fieldSets/AddFees/add-fees';
import AddTaxes from '../../fieldSets/AddTaxes/add-taxes';
import VatAddTaxes from '../../fieldSets/AddTaxes/vat-add-taxes';
import VatAddFees from '../../fieldSets/AddFees/vat-add-fees';
import { PageSpinner } from 'shared-components/decorators/spinner';
import submitCreateAddOnForm from '../../submit/submitCreateAddOnForm';
import validateCreateItemForm from '../../validate/validateCreateItemForm';
import RequiredLabel from '../../../../../elements-inputs/RequiredLabel';
import AddOnPhoto from '../../../../../elements-forms/AddOnPhoto';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Tooltip, TooltipData } from '../../../../../elements-tooltip/tooltip';

const CreateItemForm = ({
  handleSubmit = (e) => { e.preventDefault(); },
  priceOptions = [],
  subcategoryOptions = [],
  categoryOptions = [],
  currency = '',
  handleCategoryChange = null,
  addOnFeeValues = [],
  addOnTaxValues = [],
  isFetching = false,
  fromManageAddons = false,
  handlePhotoChange = null,
  addOnImgId = '',
  isEditAddOnModal,
  handleRemovePhotoClick,
  iosWebView = false,
  intl,
  isVatEnabled = false,
  intlCollectVatCopy = {},
  intlAddOnTaxTypes = [],
  inventoryError,
}) => {
  return (
    <div data-testing-id="create-item-form">
      {
        isFetching
          ? <PageSpinner additionalStyle="gray" />
          : <form className="create-item-form" onSubmit={handleSubmit}>
            {
              !iosWebView
                && <AddOnPhoto
                  addOnImgId={addOnImgId}
                  handlePhotoChange={handlePhotoChange}
                  handleRemovePhotoClick={handleRemovePhotoClick}
                  isEditAddOnModal={isEditAddOnModal}
                />
            }

            <div className="title-section">
              <RequiredLabel className="label" htmlFor="label">
                <FormattedMessage
                  id="inbox.create_item_form.name"
                  defaultMessage="Item name"
                />
              </RequiredLabel>
              <Field
                name="label"
                component={TextField}
                className="form-fields"
                attributes={{ 'data-testing-id': 'title-field' }}
                placeholder={ intl.formatMessage({ id: 'inbox.create_item_form.lunch', defaultMessage: 'eg: Lunch Buffet' }) }
              />
            </div>

            <div className="category-section">
              <RequiredLabel className="label" htmlFor="category">
                <FormattedMessage
                  id="inbox.create_item_form.category"
                  defaultMessage="Category"
                />
              </RequiredLabel>
              <Field
                component={SelectField}
                name="category"
                errorMessage={ intl.formatMessage({ id: 'inbox.create_item_form.select_category', defaultMessage: 'Please select a category' }) }
                className="form-fields"
                placeholder={ intl.formatMessage({ id: 'inbox.create_item_form.select', defaultMessage: 'Select' }) }
                options={categoryOptions}
                maxMenuHeight={180}
                onChange={handleCategoryChange}
                attributes={{ 'data-testing-id': 'category-field' }}
              />
            </div>

            <div className="subcategory-section">
              <RequiredLabel className="label" htmlFor="subcategory">
                <FormattedMessage
                  id="inbox.create_item_form.subcategory"
                  defaultMessage="Subcategory"
                />
              </RequiredLabel>
              <Field
                component={SelectField}
                name="subcategory"
                errorMessage={ intl.formatMessage({ id: 'inbox.create_item_form.select_subcategory', defaultMessage: 'Please select a subcategory' }) }
                className="form-fields"
                placeholder={ intl.formatMessage({ id: 'inbox.create_item_form.select_sub', defaultMessage: 'Select' }) }
                options={subcategoryOptions}
                maxMenuHeight={180}
                attributes={{ 'data-testing-id': 'subcategory-field' }}
              />
            </div>

            <div className="description-section">
              <RequiredLabel className="label" htmlFor="description">
                <FormattedMessage
                  id="inbox.create_item_form.description"
                  defaultMessage="Description"
                />
              </RequiredLabel>
              <Field
                name="description"
                component={TextAreaField}
                className="form-fields"
                placeholder={ intl.formatMessage({ id: 'inbox.create_item_form.sandwiches', defaultMessage: 'eg: Includes deli sandwiches, chips, coffee & dessert' }) }
                maxHeight={92}
                maxLength={2000}
                attributes={{ 'data-testing-id': 'description-field' }}
              />
            </div>

            <div className="price-section">
              <RequiredLabel className="label" htmlFor="unit_cost">
                <FormattedMessage
                  id="inbox.create_item_form.price"
                  defaultMessage="Price"
                />
              </RequiredLabel>
              <div className="form-fields">
                <div className="margin-lg-bottom">
                  <Field
                    name="unit_cost"
                    component={AddOnField}
                    isCurrency
                    label={currency}
                    leftLabel
                    fieldType="number"
                    className="form-fields price-field"
                    attributes={{ 'data-testing-id': 'price-field' }}
                  />
                </div>
                <Field
                  component={SelectField}
                  name="unit_cost_basis"
                  className="form-fields"
                  options={priceOptions}
                  errorMessage={ intl.formatMessage({ id: 'inbox.create_item_form.selection', defaultMessage: 'Please make a selection' }) }
                  defaultValue={{ value: 'per_item', label: 'per item' }}
                  attributes={{ 'data-testing-id': 'unit-cost-basis-field' }}
                />
                {
                  isVatEnabled &&
                  <p className="text-small text-gray-600 margin-lg-top" data-testing-id="collect-vat-copy">
                    <FormattedMessage { ...intlCollectVatCopy } />
                  </p>
                }
              </div>
            </div>

            <div className="max-quantity-section">
              <label className="label" htmlFor="count">
                <FormattedMessage
                  id="inbox.create_item_form.quantity"
                  defaultMessage="Max quantity"
                />
              </label>
              <div className="form-fields">
                <div className="flex">
                  <div className="flex-basis-60 margin-md-right">
                    <Field
                      name="count"
                      component={NumberField}
                      attributes={{ 'data-testing-id': 'quantity-field' }}
                      placeholder={ intl.formatMessage({ id: 'inbox.create_item_form.optional', defaultMessage: 'Optional' }) }
                    />
                  </div>
                  <div className="flex-basis-40" />
                </div>
              </div>
            </div>

            <div className="tax-section">
              <label className="label flex middle" htmlFor="taxes" style={{ gap: 4 }}>
                <div className="capitalize-first-letter">
                  <FormattedMessage
                    id="inbox.create_item_form.tax"
                    defaultMessage="Tax"
                  />
                </div>
                <Tooltip tooltip={TooltipData.ADD_ONS_TAX} />
              </label>
              {
                isVatEnabled ?
                  <FieldArray
                    name="taxes"
                    component={VatAddTaxes}
                    classnames="form-fields"
                    addAddtlTaxes
                    intlAddOnTaxTypes={intlAddOnTaxTypes}
                    addOnTaxValues={addOnTaxValues}
                    taxError={inventoryError}
                  /> :
                  <FieldArray
                    name="taxes"
                    component={AddTaxes}
                    classnames="form-fields"
                    addAddtlTaxes
                    taxError={inventoryError}
                  />
              }
            </div>

            <div className="fees-section">
              <label className="label margin-sm-right" htmlFor="fees">
                <FormattedMessage
                  id="inbox.create_item_form.fees"
                  defaultMessage="Fees"
                />
              </label>
              <span className="text-small text-gray-600">
                (<FormattedMessage
                  id="inbox.add_fees.delivery"
                  defaultMessage="Gratuity, delivery, etc."
                />)
              </span>
              <FieldArray
                name="fees"
                currency={currency}
                component={isVatEnabled ? VatAddFees : AddFees}
                classnames="form-fields"
                addOnFeeValues={addOnFeeValues}
                addAddtlFees
                isVatEnabled={isVatEnabled}
              />
            </div>

            <div className="listing-visibility-section">
              <RequiredLabel className="label" htmlFor="visible_on_listing">
                <FormattedMessage
                  id="inbox.create_item_form.show"
                  defaultMessage="Show on listings?"
                />
              </RequiredLabel>
              <Field
                name="visible_on_listing"
                component={ToggleField}
                attributes={{ 'data-testing-id': 'toggle-listing-field' }}
                firstOption={
                  {
                    id: 'show-listing-true',
                    value: 'true',
                    label: intl.formatMessage({ id: 'inbox.create_item_form.show_yes', defaultMessage: 'Yes' }),
                    attributes: { 'data-testing-id': 'show-listing-true' }
                  }}
                secondOption={
                  {
                    id: 'show-listing-false',
                    value: 'false',
                    label: intl.formatMessage({ id: 'inbox.create_item_form.show_no', defaultMessage: 'No' }),
                    attributes: { 'data-testing-id': 'show-listing-false' }
                  }
                }
              />
              <p className="text-small text-gray-600 margin-lg-top">
                Showing this item on your listing will make it available for purchase. {!fromManageAddons
                  ? <FormattedMessage id="inbox.create_item_form.manage_addons" defaultMessage="You can manage your add-ons from the Add-on Inventory page." />
                  : ''}
              </p>
            </div>

            <hr className="no-bottom-margin" />
          </form>
      }
    </div>
  );
};

CreateItemForm.propTypes = {
  currency: PropTypes.string,
  addOnFeeValues: PropTypes.array,
  handleCategoryChange: PropTypes.func,
  categoryOptions: PropTypes.array,
  priceOptions: PropTypes.array,
  subcategoryOptions: PropTypes.array,
  handleSubmit: PropTypes.func,
  isFetching: PropTypes.bool,
  fromManageAddons: PropTypes.bool,
  handlePhotoChange: PropTypes.func,
  addOnImgId: PropTypes.string,
  isEditAddOnModal: PropTypes.bool,
  handleRemovePhotoClick: PropTypes.func,
  iosWebView: PropTypes.bool,
  intl: intlShape,
  isVatEnabled: PropTypes.bool,
  intlAddOnTaxTypes: PropTypes.array,
  addOnTaxValues: PropTypes.array
};

const config = {
  form: CREATE_ITEM_FORM,
  onSubmit: submitCreateAddOnForm,
  validate: validateCreateItemForm
};

export default injectIntl(reduxForm(config)(CreateItemForm));
